import { Component, inject } from '@angular/core';
import { ModalController, IonicModule } from '@ionic/angular';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';

export interface version {
  version: string;
  date: Date;
  description?: string;
  changes?: string[];
}

export const VERSIONS: version[] = [
  {
    version: '5.10.0',
    date: new Date('2026-03-16'),
    description: `
      <br><br>
      <h1>Katalog+ jetzt direkt in der App</h1>
      <br>
      Die <strong>Literatursuche der Universitätsbibliothek (Katalog+)</strong> ist nun ebenfalls in die App eingebunden. So könnt ihr schnell und unkompliziert nach Literatur suchen – direkt vom Smartphone aus.
    `,
    changes: [
      'Verbesserung der Synchronisation der Kacheleinstellungen bei Verwendung mehrerer Endgeräte. Synchronisiert werden immer nur Endgeräte mit der aktuellen Version.',
      'Bugfixes und Verbesserungen'
    ]
  },
  {
    version: '5.9.0',
    date: new Date('2025-12-08'),
    description: `
    Mit dem aktuellen Update haben wir die TU App weiter für euch verbessert, um euren Studienalltag noch leichter zu machen. Das erwartet euch:
    <br><br>

    <h1>Seminarräume als Lernräume nutzen</h1>
    <br>
    Ab sofort zeigt euch die App nicht nur die bekannten Lernräume an, sondern auch <strong>Seminarräume, die außerhalb von Veranstaltungen zum Lernen frei sind</strong>.
    In der App findet ihr jetzt:

    <ul>
      <li>welche Seminarräume sich auf dem Campus befinden</li>
      <li>wann dort Lehrveranstaltungen stattfinden. Damit wisst ihr genau, wann ihr einen Raum zum Lernen nutzen könnt. Die Anzeige der klassischen Lernräume bleibt natürlich bestehen – so habt ihr jetzt alle Lernmöglichkeiten am Campus auf einen Blick.</li>
    </ul>

    <h1>Neue FAQ</h1>
    <br>
    Frisch integrierte <strong>FAQ</strong> beantworten die wichtigsten Fragen zur Nutzung der App. Falls ihr etwas nicht findet oder Funktionen unklar sind, schaut einfach dort rein.
    
    <br><br>
    <h1>Allgemeine Verbesserungen</h1>
    Außerdem haben wir kleinere <strong>Anpassungen und Optimierungen</strong> vorgenommen, um die App stabiler und angenehmer in der Nutzung zu machen.
    `
  },
  {
    version: '5.8.1',
    date: new Date('2025-10-14'),
    changes: [
      'Bugfixes und Verbesserungen'
    ]
  },
  {
    version: '5.8.0',
    date: new Date('2025-10-01'),
    description: `
    <strong>Semesterticket immer griffbereit</strong>
    Ticketkontrolle? Kein Stress mehr! Du kannst dein Ticket jetzt direkt im Wallet deines Handys speichern und vorzeigen – ganz ohne die App zu öffnen. Gute Fahrt!
    `,
    changes: [
      'Öffnungszeiten der Mensen können abgerufen werden',
      'Push Benachrichtigungen für den Gesundheitspass hinzugefügt',
      'Bugfixes und Verbesserungen'
    ]
  },
  {
    version: '5.7.0',
    date: new Date('2025-07-03'),
    description: `
    <strong>Einschreibe- und Rückmeldestatus im Überblick</strong>
    <br/><br/>
    Die App zeigt euch jetzt direkt beim Semesterticket, ob ihr euch Informationen zu eurem Einschreibe- bzw. Rückmeldestatus an und ob noch etwas fehlt. So wisst ihr immer, wie der Stand ist – und habt alles im Blick, sobald ihr das Ticket seht.<br/>
    Ihr werdet darüber informiert…
    <ul>
    <li>…ob es ausstehende Rechnungen gibt</li>
    <li>…ob benötigte Versicherungsunterlagen noch nicht eingereicht wurden</li>
    <li>…ob ihr immatrikuliert oder exmatrikuliert seid</li>
    </ul>
    `,
  },
  {
    version: '5.6.0',
    date: new Date('2025-06-11'),
    description: `
    <strong>Schadensmeldung leicht gemacht</strong>
    <br/><br/>
    Du hast einen Schaden am Campus entdeckt? Einfach ein Foto machen, Standort angeben und direkt über die App ans Gebäudemanagement melden. Schneller und unkomplizierter ging’s noch nie!
    `,
  },
  {
    version: '5.5.0',
    date: new Date('2025-05-21'),
    description: `
    <strong>Mach die App zu deiner App</strong>
    <br/>
    Zu viele Kacheln? Jetzt kannst du selbst entscheiden, welche Funktionen du siehst und in welcher Reihenfolge. Die Unterscheidung zwischen öffentlichem und internem Bereich fällt weg – und das Beste: Deine Einstellungen bleiben auch erhalten, wenn du dich auf einem neuen Gerät anmeldest.
    <br/><br/>
    <strong>Uni-Kino immer dabei</strong>
    <br/>
    Wusstest du, dass es am Campus ein eigenes Kinoprogramm gibt? Ab sofort findest du das aktuelle Programm vom Uni-Film-Club direkt in der App. Viel Spaß bei deinem nächsten Kinoabend!  
    `
  },
  {
    version: '5.4.0',
    date: new Date('2025-02-07'),
    description: `
    <strong>Gesundheitspass</strong>
    <br/><br/>
    Der Gesundheitspass für Beschäftigte ist nun auch digital verfügbar: Ab sofort können Sie für die Teilnahme an Gesundheitsangeboten in der TU Dortmund-App Stempel sammeln. Diese Neuerung ermöglicht es Ihnen, den Gesundheitspass bequem und sicher auf dem Smartphone zu verwalten.
    `,
    changes: [
      'Die Übersicht über den UB-Account wurde an die aktuelle Version des Katalog+ angepasst.',
      'Bugfixes und Verbesserungen'
    ]
  },
  {
    version: '5.2.0',
    date: new Date('2024-08-01'),
    description: `
    <strong>Campus-Wetter</strong>
    <br/><br/>
    Lieber Pulli oder T-Shirt? Die TU Dortmund-App zeigt dir jetzt das Campus-Wetter. Die Anzeige kannst du dir so einrichten, wie du es bevorzugst.
    `,
  },
  {
    version: '5.1.0',
    date: new Date('2024-01-29'),
    description: `
    <strong>Was gibt es Neues für dich in der App?</strong>
    <ul>
      <li>
        Es gibt jetzt eine Kachel, auf der du alle Lernorte am Campus Nord, Süd und in der Innenstadt siehst. Bei ausgewählten Lernorten kannst du sehen, wie ausgelastet sie sind.
      </li>
      <li>
        Wir haben das Campusnavi komplett renoviert:
        <ul>
          <li>Du kannst jetzt stufenlos zoomen und drehen</li>
          <li>Die Darstellung der Karte ist jetzt übersichtlicher</li>
          <li>Du kannst die Karte mit einer Wischgeste (mit zwei Fingern nach oben wischen) kippen</li>
          <li>Auch hier werden die Lernorte mit Auslastung angezeigt</li>
        </ul>
      </li>
      <li>Und wie immer: kleinere Verbesserungen und Bugfixes</li>
    </ul>
    `,
  },
  {
    version: '5.0.0',
    date: new Date('2023-11-22'),
    changes: [
      'Bugfixes und Verbesserungen'
    ]
  }
];

@Component({
    selector: 'app-changelog',
    templateUrl: './changelog.page.html',
    styleUrls: ['./changelog.page.scss'],
    imports: [IonicModule, DatePipe, TranslatePipe]
})
export class ChangelogPage {
  modalController = inject(ModalController);
  translate = inject(TranslateService);

  public versions = VERSIONS;

  public getLocale(): string {
    switch (this.translate.currentLang()) {
      case 'de-DE': {
        return 'de';
      }

      case 'en-US': {
        return 'en';
      }

      default: {
        return 'de';
      }
    }
  }
}
